// Public SMS Terms page — required for CTIA / toll-free carrier compliance.
import React from "react";
import { Link } from "react-router-dom";

export default function SmsTerms() {
  return (
    <main className="min-h-screen bg-bone py-16 px-4" data-testid="sms-terms-page">
      <div className="max-w-3xl mx-auto bg-white rounded-xl border border-[#E2E0D8] p-8 sm:p-12 shadow-sm">
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-brandblue">MedexOVA Medical Transportation</div>
        <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-black text-slate_navy">SMS Messaging Terms &amp; Conditions</h1>
        <p className="text-xs text-slate-500 mt-2">Last updated: June 2026</p>

        <div className="prose prose-slate mt-8 text-sm leading-relaxed text-slate_navy space-y-4">
          <section>
            <h2 className="font-heading text-lg font-bold text-slate_navy mt-6">1. Program Description</h2>
            <p>
              Medex Transportation Services LLC ("MedexOVA", "we", "us") operates an SMS notification
              program to send transactional messages related to your non-emergency medical transportation request.
              By opting in, you consent to receive automated text messages from MedexOVA at the mobile phone
              number you provide when submitting a trip request.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-slate_navy mt-6">2. Types of Messages You May Receive</h2>
            <ul className="list-disc ml-6 space-y-1">
              <li>Trip request received confirmation</li>
              <li>Trip confirmation / decline / reschedule prompts</li>
              <li>Driver assignment with name and estimated arrival time</li>
              <li>"Driver en route", "driver arrived", "pickup complete", "drop-off complete" status notifications</li>
              <li>Schedule changes or trip cancellations</li>
              <li>Reminders for upcoming dialysis, appointment, or recurring trips</li>
            </ul>
            <p className="mt-2">All messages are transactional and directly related to a trip you or your facility has requested. We do not send promotional or marketing SMS.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-slate_navy mt-6">3. Message Frequency</h2>
            <p>Message frequency varies based on your trip activity. You may receive up to 8 messages per trip (request confirmation, driver assignment, en route, arrived, pickup, drop-off, return-leg coordination, and a courtesy follow-up).</p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-slate_navy mt-6">4. Message and Data Rates</h2>
            <p>
              <b>Standard message and data rates may apply.</b> MedexOVA does not charge for SMS messages, but your
              mobile carrier may charge you for sending or receiving text messages depending on your plan. Contact
              your wireless provider for details about your messaging plan.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-slate_navy mt-6">5. How to Opt Out (STOP)</h2>
            <p>
              You can opt out of MedexOVA SMS messages at any time by replying <b>STOP</b>, <b>END</b>, <b>QUIT</b>, <b>CANCEL</b>, or <b>UNSUBSCRIBE</b> to any message we send you. After you opt out, you will receive a single confirmation message and will not receive further SMS messages from MedexOVA unless you opt back in.
            </p>
            <p>To opt back in, reply <b>START</b> or <b>YES</b> to MedexOVA at <a href="tel:+18336592699" className="text-brandblue font-semibold">1-833-659-2699</a>, or submit a new trip request and check the SMS consent box.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-slate_navy mt-6">6. Need Help? (HELP)</h2>
            <p>For help, reply <b>HELP</b> to any MedexOVA SMS message. You can also reach our dispatch team:</p>
            <ul className="list-none ml-0 mt-2 space-y-1">
              <li>Phone: <a href="tel:+18336592699" className="text-brandblue font-semibold">1-833-659-2699</a></li>
              <li>Email: <a href="mailto:info@medexova.com" className="text-brandblue font-semibold">info@medexova.com</a></li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-slate_navy mt-6">7. Carrier Disclaimer</h2>
            <p>
              Wireless carriers (AT&amp;T, Verizon, T-Mobile, Sprint, U.S. Cellular, MetroPCS, Boost, Cricket, Virgin
              Mobile, and others) are not liable for any delayed or undelivered messages.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-slate_navy mt-6">8. Privacy &amp; Data Sharing</h2>
            <p>
              <b>Your mobile information will not be sold or shared with third parties for promotional or
              marketing purposes.</b> We may share your number with our subcontracted drivers, partner dispatch
              network (OVA Dispatch), and SMS service provider (Brevo / SignalWire) solely to fulfill your trip
              request. See our <Link to="/privacy" className="text-brandblue underline font-semibold">Privacy Policy</Link> for full details.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-slate_navy mt-6">9. Eligibility</h2>
            <p>You must be at least 18 years old, or the legal guardian / authorized contact of the rider, to opt into MedexOVA SMS messages.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-slate_navy mt-6">10. Changes to This Policy</h2>
            <p>We may update these terms periodically. Continued use of the SMS service after updates constitutes acceptance of the revised terms.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-bold text-slate_navy mt-6">Contact</h2>
            <p>
              Medex Transportation Services LLC<br />
              <a href="tel:+18336592699" className="text-brandblue font-semibold">1-833-659-2699</a> · <a href="mailto:info@medexova.com" className="text-brandblue font-semibold">info@medexova.com</a>
            </p>
          </section>
        </div>

        <div className="mt-12 pt-6 border-t border-[#E2E0D8] flex items-center justify-between">
          <Link to="/" className="text-sm text-brandblue hover:text-brandblue-hover font-semibold">← Back to home</Link>
          <Link to="/book" className="text-sm bg-forest hover:bg-forest-hover text-white px-4 py-2 rounded-md font-semibold">Book a trip</Link>
        </div>
      </div>
    </main>
  );
}
